import { alpha, createTheme } from '@mui/material/styles';
import { common, grey } from '@mui/material/colors';
export interface IColors {
  primary: '#27AAE1';
  secondary: '#546eb2';
  green: '#00AF68';
  blue: '#356CEC';
  disabled: (typeof grey)[300];
  white: '#FFFFFF';
  red: '#D32F2F';
  muted: '#333333';
  /**
   * CLD-2970 conflict tones, taken from the CONC-5 mockup: grey =
   * unchanged/agree, blue = Mine, brown = Theirs, red = conflict, resolved =
   * decided this session.
   *
   * Each tone is a pale fill plus the dark text colour that goes on it. The
   * mockup fills cells with tints and carries the meaning in the text colour,
   * rather than flooding them with saturated colour — which is why these are
   * no longer the brand `blue`/`red`. Keep in step with `conflictGrid.scss`.
   */
  conflict: {
    grey: ConflictTone;
    blue: ConflictTone;
    brown: ConflictTone;
    red: ConflictTone;
    resolved: ConflictTone;
  };
}

export interface ConflictTone {
  /** Cell fill. */
  bg: string;
  /** Text on that fill. */
  fg: string;
}

/**
 * Mockup chrome for the resolve screen, scoped to that feature rather than
 * promoted to the app palette — `colors.primary` is the Trinity brand and the
 * mockup's own accent is not it.
 */
export const mergeChrome = {
  /** Grid header row and button faces. */
  surface: '#F3F4F6',
  /** Row and section dividers. */
  divider: '#E5E7EB',
  /** Heavier dividers: panel edges, the footer rule. */
  dividerStrong: '#D1D5DB',
  /** Body and header text. */
  text: '#1F2937',
  /** Secondary text: the version line, captions. */
  textMuted: '#4B5563',
} as const;

export const colors: IColors = {
  primary: '#27AAE1',
  secondary: '#546eb2',
  green: '#00AF68',
  blue: '#356CEC',
  disabled: grey[300],
  white: '#FFFFFF',
  red: '#D32F2F',
  muted: '#333333',
  conflict: {
    grey: { bg: '#EEEEEE', fg: '#1F2937' },
    blue: { bg: '#DCE6F1', fg: '#366092' },
    brown: { bg: '#DDD9C4', fg: '#632523' },
    red: { bg: '#F2DCDB', fg: '#632523' },
    resolved: { bg: '#FFFFFF', fg: '#1F2937' },
  },
};

declare module '@mui/material/styles/createPalette' {
  interface CommonColors {
    green: string;
    blue: string;
    disabled: string;
    white: string;
    red: string;
    muted: string;
  }
}

/**
 * @type {import('@mui/material').ThemeOptions}
 */
export const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 450,
      md: 600,
      lg: 1200,
      xl: 1600,
    },
  },
  typography: {
    h1: {
      fontSize: '3.75rem',
      fontWeight: 300,
    },
    h2: {
      fontSize: '3rem',
      fontWeight: 400,
    },
    h3: {
      fontSize: '2.125rem',
      fontWeight: 400,
    },
    h4: {
      fontSize: '1.75rem',
      fontWeight: 400,
    },
    h5: {
      fontSize: '1.5rem',
      fontWeight: 400,
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 600,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 600,
    },
  },
  palette: {
    primary: {
      main: colors.primary,
    },
    secondary: {
      main: colors.secondary,
    },
    common: {
      green: colors.green,
      blue: colors.blue,
      disabled: colors.disabled,
      white: colors.white,
      red: colors.red,
      muted: colors.muted,
    },
    error: {
      main: colors.red,
    },
  },
  components: {
    MuiButton: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          borderRadius: '30px',
        },
        containedPrimary: {
          color: colors.white,
        },
      },
    },
    MuiCard: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '$labelcolor',
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          height: '58px',
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: colors.primary,
        },
        colorSecondary: {
          '&$checked': {
            color: colors.primary,
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            borderLeft: `5px solid ${colors.secondary}`,
            backgroundColor: alpha(colors.secondary, 0.1),
            '&:hover': {
              backgroundColor: alpha(colors.secondary, 0.1),
            },
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        colorSecondary: {
          color: colors.primary,
          '&$checked': {
            color: colors.primary,
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          '&:hover': {
            color: 'none',
          },
          verticalAlign: 'middle',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: colors.secondary,
            color: colors.white,
            '&:hover': {
              backgroundColor: colors.secondary,
            },
          },
          '&.Mui-disabled': {
            backgroundColor: colors.disabled,
          },
          height: '2rem',
          '&:hover': {
            backgroundColor: 'none',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(colors.primary, 0.1),
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            backgroundColor: colors.disabled,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            backgroundColor: colors.disabled,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: `rgba(39, 170, 225, 0.08)`,
            fontWeight: 600,
          },
          backgroundColor: '#E8EDF3',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#304659',
          color: common.white,
          border: '1px solid #dadde9',
          '& .TrinityTooltip-arrow': {
            color: '#304659',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        colorPrimary: ({ theme }) => ({
          color: theme.palette.primary.main,
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
        }),
        colorSecondary: ({ theme }) => ({
          color: theme.palette.secondary.main,
          backgroundColor: alpha(theme.palette.secondary.main, 0.1),
        }),
        colorSuccess: ({ theme }) => ({
          color: theme.palette.success.main,
          backgroundColor: alpha(theme.palette.success.main, 0.1),
        }),
        colorError: ({ theme }) => ({
          color: theme.palette.error.main,
          backgroundColor: alpha(theme.palette.error.main, 0.1),
        }),
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        message: {
          width: '100%',
        },
      },
    },
    // ---------------------------------------------------------------------
    // MUI x-date-pickers: route the picker's "selected" accents at
    // `secondary` (#546eb2 purple-blue) instead of `primary` (#27AAE1
    // cyan-teal). Primary is overloaded with the Confirm CTA and reads as
    // distinctly green for deuteranomalous viewers.
    //
    // We do this via MuiCssBaseline + global selectors targeting the
    // codebase's Trinity-prefixed runtime class names. We initially tried
    // theme.components.MuiPickersYear styleOverrides but MUI's styled()
    // engine in @mui/x-date-pickers v6 did NOT merge theme overrides into
    // its emitted CSS for those components (verified live via Playwright
    // — none of our override rules appeared in document.styleSheets).
    // Global selectors with !important are the load-bearing path that
    // actually wins specificity against the component's internal emotion
    // class without requiring x-date-pickers to participate in the theme.
    // ---------------------------------------------------------------------
    MuiCssBaseline: {
      styleOverrides: {
        // Selected year/month/day buttons inside any date picker on this
        // app. Targets BOTH the Mui-prefixed class (used by the package
        // internally) AND the Trinity-prefixed one (emitted at runtime
        // by MuiClassNameSetup.ts). Buttons carry both .Mui-selected and
        // the component-specific class.
        '.TrinityPickersYear-yearButton.Mui-selected, .MuiPickersYear-yearButton.Mui-selected, .TrinityPickersMonth-monthButton.Mui-selected, .MuiPickersMonth-monthButton.Mui-selected':
          {
            color: `${common.white} !important`,
            backgroundColor: `${colors.secondary} !important`,
          },
        '.TrinityPickersYear-yearButton.Mui-selected:hover, .TrinityPickersYear-yearButton.Mui-selected:focus, .MuiPickersYear-yearButton.Mui-selected:hover, .MuiPickersYear-yearButton.Mui-selected:focus, .TrinityPickersMonth-monthButton.Mui-selected:hover, .TrinityPickersMonth-monthButton.Mui-selected:focus, .MuiPickersMonth-monthButton.Mui-selected:hover, .MuiPickersMonth-monthButton.Mui-selected:focus':
          {
            backgroundColor: `${colors.secondary} !important`,
          },
        '.TrinityPickersDay-root.Mui-selected, .MuiPickersDay-root.Mui-selected':
          {
            color: `${common.white} !important`,
            backgroundColor: `${colors.secondary} !important`,
          },
        '.TrinityPickersDay-root.Mui-selected:hover, .TrinityPickersDay-root.Mui-selected:focus, .MuiPickersDay-root.Mui-selected:hover, .MuiPickersDay-root.Mui-selected:focus':
          {
            backgroundColor: `${colors.secondary} !important`,
          },
        '.TrinityPickersDay-today, .MuiPickersDay-today': {
          borderColor: `${colors.secondary} !important`,
        },
      },
    },
  },
});
